import { ArrowRight } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { Input } from "~/components/ui/Input";

export const NewsletterForm = ({
  showTitle = true,
  title,
  description,
  className,
  inputClassName,
  titleClassName,
  placeholder,
}: {
  showTitle?: boolean;
  title?: string;
  description?: any;
  className?: string;
  inputClassName?: string;
  titleClassName?: string;
  placeholder?: string;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionData, setActionData] = useState<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  const isSuccess = actionData?.sent === true;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setActionData(null);

    const formData = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      setActionData(data);
    } catch (error) {
      setActionData({
        ok: false,
        message: "Si è verificato un errore imprevisto.",
      });
      console.error("Errore durante l'iscrizione alla newsletter:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      formRef.current?.reset();
    }
  }, [isSuccess]);

  if (isSuccess) {
    return (
      <p className="text-green-700 bg-white px-2 py-2 text-sm mt-4">
        {t("Iscrizione avvenuta con successo!")}
      </p>
    );
  }

  return (
    <div className={[className].filter(Boolean).join(" ")}>
      {showTitle && (
        <>
          <h5
            className={[
              "font-bold uppercase text-sm mb-1 text-secondary",
              titleClassName,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {title ?? t("Iscriviti alla newsletter")}
          </h5>
          {description && (
            <div className="text-md text-secondary-foreground/80 mb-2 mt-4">
              {description}
            </div>
          )}
        </>
      )}
      <form onSubmit={handleSubmit} ref={formRef}>
        <div
          className={[
            "bg-white flex flex-row mt-4 items-center pr-2",
            inputClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={placeholder ?? t("Email")}
            className={[
              "border-none focus-visible:ring-0 text-black placeholder:text-secondary-200",
            ]
              .filter(Boolean)
              .join(" ")}
            required
            autoComplete="email"
          />
          <Button
            variant={"secondary"}
            className="p-1 h-auto"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              "..."
            ) : (
              <ArrowRight absoluteStrokeWidth size={13} />
            )}
          </Button>
        </div>
      </form>
      {actionData?.ok === false && (
        <p className="text-red-600 text-sm mt-2">{actionData.message}</p>
      )}
    </div>
  );
};
